import { Box, Heading, useToast, Icon, HStack } from '@chakra-ui/react';
import { RiAddLine } from 'react-icons/ri';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { LinkButton } from '../../../../../components/LinkButton';
import {
  maskCpf,
  maskShortDate,
} from '../../../../../utils/formatters/handleMask';
import { ILodger, LodgerTable } from './components/LodgerTable';
import { listUsersService } from '../../../../../services/Users/ListUsersService';
import { translateError } from '../../../../../utils/errors';
import { useAuth } from '../../../../../hooks/auth';

interface ILoadLodgersProps {
  cpf?: string;
  email?: string;
  name?: string;
  phone?: string;
  ventureId?: string;
  sort?: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt';
  order?: 'ASC' | 'DESC';
  page?: number;
}

export const LodgerList = (): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment, venture: authenticatedVenture } =
    useAuth();

  const [lodgersList, setLodgersList] = useState<ILodger[]>([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>(
    'DESC',
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('name');
  const [sortBy, setSortBy] = useState<
    'cpf' | 'name' | 'phone' | 'email' | 'createdAt'
  >('createdAt');
  const [totalPages, setTotalPages] = useState<number>();
  const [totalItems, setTotalItems] = useState<number>();
  const [showDeleted, setShowDeleted] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const canReadDeleted =
    !!authenticatedAssignment?.featureGroup.key.includes('MANAGER');

  const loadLodgers = useCallback(
    async ({
      cpf,
      email,
      name,
      phone,
      page,
      sort,
      order,
    }: ILoadLodgersProps) => {
      try {
        const lodgers = await listUsersService({
          cpf: cpf || undefined,
          email: email || undefined,
          name: name || undefined,
          phone: phone || undefined,
          featureGroups: ['LODGER'],
          assignmentStatus: showDeleted ? 'DELETED' : undefined,
          limit: 20,
          page,
          sort,
          order,
        });

        const parsedLodgers = lodgers.items.map((lodger) => ({
          ...lodger,
          cpf: lodger.cpf ? maskCpf(lodger.cpf) : undefined,
          formattedDate: maskShortDate(lodger.createdAt),
        }));

        setTotalPages(lodgers.pages);
        setTotalItems(lodgers.total);
        setLodgersList(parsedLodgers);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados dos membros, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast, showDeleted],
  );

  useEffect(() => {
    loadLodgers({
      ...currentFilters,
      order: currentOrdering,
      page: currentPage,
      sort: sortBy,
    });
  }, [
    currentFilters,
    currentPage,
    sortBy,
    loadLodgers,
    currentOrdering,
    authenticatedVenture,
    reloadKey,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters({ [filterBy]: value });
    },
    [filterBy],
  );

  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleSortList = useCallback(
    (sort: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt') => {
      setSortBy(sort);
    },
    [],
  );

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleToggleShowDeleted = useCallback(() => {
    setShowDeleted((prevState) => !prevState);
    setCurrentPage(1);
  }, []);

  const handleReload = useCallback(() => {
    setReloadKey((prevState) => prevState + 1);
  }, []);

  return (
    <DefaultLayout>
      <Box flex="1" borderRadius={8} bg="white" p="8">
        <HStack justify="space-between">
          <Heading size="lg" fontWeight="normal">
            Hóspedes
          </Heading>

          {authenticatedAssignment?.featureGroup.features.some((feature) =>
            ['LODGER_FULL_ACCESS', 'LODGER_WRITE_ACCESS'].includes(feature.key),
          ) && (
            <LinkButton
              colorScheme="green"
              icon={<Icon as={RiAddLine} fontSize="16" />}
              to="/lodgers/register"
            >
              Cadastrar novo
            </LinkButton>
          )}
        </HStack>

        <LodgerTable
          mt="4"
          lodgers={lodgersList}
          showDeleted={showDeleted}
          onToggleShowDeleted={
            canReadDeleted ? handleToggleShowDeleted : undefined
          }
          onAssignmentChanged={handleReload}
          currentPage={currentPage}
          filterBy={filterBy}
          onChangeFilterBy={handleFilterBy}
          onFilter={debounceHandleFilter}
          onOrder={handleOrderList}
          onPageChange={handlePageChange}
          onSort={handleSortList}
          order={currentOrdering}
          sortBy={sortBy}
          totalPages={totalPages}
          totalItems={totalItems}
        />
      </Box>
    </DefaultLayout>
  );
};
